import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
// api
import { activateAPI, forgetPasswordAPI, getUserAPI, loginAPI, logoutAPI, queryIsEnableRegisterAPI, registerAPI, resendActivateEmailAPI, resetPasswordJWTAPI, updateRestoreIDAPI, updateUserAPI } from "src/api";
// hooks
import { useWaitForDatalayer } from "src/hooks/use-wait-for-datalayer";
import { useWaitForFcWidget } from "src/hooks/use-wait-for-fcWidget";
// redux
import { persistor } from "src/redux/store";
//
import { AuthContext } from "./auth-context";
import { isValidToken, setSession } from "./utils";

// ----------------------------------------------------------------------

const initialState = {
    loading: true,
    //
    user: null
};

const reducer = (state, action) => {
    if (action.type === "INITIAL") {
        const { user } = action.payload;

        return {
            loading: false,
            user
        };
    }

    if (action.type === "LOGIN") {
        return {
            ...state
        };
    }

    if (action.type === "LOGOUT") {
        const { user } = action.payload;

        return {
            ...state,
            user
        };
    }

    if (action.type === "MODIFY_USER") {
        const { user } = action.payload;

        const target = {
            ...state.user,
            ...user
        };

        return {
            ...state,
            user: target
        };
    }

    return state;
};

// // Actions
// export const { LOGOUT } = slice.actions;

// ----------------------------------------------------------------------

const STORAGE_KEY = "accessToken";

export function AuthProvider({ children }) {
    const [ state, dispatch ] = useReducer(reducer, initialState);

    const initialize = useCallback(async () => {
        try {
            const accessToken = localStorage.getItem(STORAGE_KEY);

            if (accessToken && isValidToken(accessToken)) {
                await setSession(accessToken);

                const storedUser = JSON.parse(localStorage.getItem("user") || "null");
                const { data } = await getUserAPI();

                dispatch({
                    type: "INITIAL",
                    payload: {
                        user: {
                            ...storedUser,
                            ...data?.data
                        }
                    }
                });
            } else {
                dispatch({
                    type: "INITIAL",
                    payload: {
                        user: null
                    }
                });
            }
        } catch (e) {
            dispatch({
                type: "INITIAL",
                payload: {
                    user: null
                }
            });
        }
    }, []);

    const getUser = useCallback(async () => {
        try {
            const user = JSON.parse(localStorage.getItem("user"));

            const { status, data } = await getUserAPI();

            if (status === 200) {
                dispatch({
                    type: "MODIFY_USER",
                    payload: {
                        user: {
                            ...user,
                            ...( status === 200 && ( data?.data ?? {} ) )
                        }
                    }
                });
            }
        } catch (e) {
            console.error(e);
        }
    }, []);

    const handelLogin = useCallback(async (data) => {
        const accessToken = data?.jwt;
        const user = data?.jwt_payload;

        localStorage.removeItem("email-recovery");

        if (accessToken) {
            await setSession(accessToken);
        }

        if (JSON.stringify(user)) {
            localStorage.setItem("user", JSON.stringify(user));
        } else {
            localStorage.removeItem("user");
        }

        await getUser();
    }, [ getUser ]);

    const [ isDatalayerMounted, setIsDatalayerMounted ] = useState(false);
    const [ isFcWidgetMounted, setIsFcWidgetMounted ] = useState(false);

    useWaitForDatalayer(() => setIsDatalayerMounted(true));
    useWaitForFcWidget(() => setIsFcWidgetMounted(true));

    // REGISTER
    const register = useCallback(async (request) => {
        const { status, data } = await registerAPI({ ...request });

        if (status === 200) {
            await handelLogin(data?.data);

            dispatch({ type: "LOGIN" });
        }
    }, [ handelLogin ]);

    // LOGIN
    const login = useCallback(async (request) => {
        const { status, data } = await loginAPI({ ...request });

        if (status === 200) {
            await handelLogin(data?.data);

            dispatch({ type: "LOGIN" });
        }
    }, [ handelLogin ]);

    // LOGOUT
    const logout = useCallback(async () => {
        const response = await logoutAPI();

        if (response.status === 200) {
            await setSession(null);

            dispatch({
                type: "LOGOUT",
                payload: {
                    user: null
                }
            });

            localStorage.clear();

            persistor.purge().then(null);
        }
    }, []);

    const kicked = useCallback(async () => {
        await setSession(null);

        dispatch({
            type: "LOGOUT",
            payload: {
                user: null
            }
        });

        localStorage.clear();

        persistor.purge().then(null);
    }, []);

    // Forgot Password
    const forgotPassword = useCallback(async (request) => {
        const { status } = await forgetPasswordAPI({ ...request });

        return status === 200;
    }, []);

    const resetPassword = useCallback(async (request) => {
        const { status } = await resetPasswordJWTAPI({ ...request });

        return status === 200;
    }, []);

    const queryIsEnableRegister = useCallback(async () => {
        const res = await queryIsEnableRegisterAPI();
        return res?.data?.data;
    }, []);

    // Active User
    const activeUser = useCallback(async (request) => {
        const { status, data } = await activateAPI({ ...request });

        if (status === 200) {
            await handelLogin(data?.data);

            dispatch({ type: "LOGIN" });
        }

        return status === 200;
    }, [ handelLogin ]);

    // Resend Email
    const resendActivateEmail = useCallback(async (email) => {
        const { status } = await resendActivateEmailAPI({ email });

        return status === 200;
    }, []);

    // User
    const updateUser = useCallback((detail) => {
        dispatch({
            type: "MODIFY_USER",
            payload: {
                user: {
                    ...detail
                }
            }
        });
    }, []);

    const modifyUser = useCallback(async (detail) => {
        const response = await updateUserAPI(detail);

        const { status } = response;

        if (status === 200) {
            dispatch({
                type: "MODIFY_USER",
                payload: {
                    user: {
                        first_name: detail?.first_name ?? "",
                        last_name: detail?.last_name ?? "",
                        email: detail?.email ?? "",
                        company: detail?.company ?? "",
                        country: detail?.country ?? "",
                        phone: detail?.phone ?? "",
                        official_website: detail?.official_website ?? ""

                        // warehouse_id: detail?.warehouse ?? "",
                        // timezone: detail?.timezone ?? ""
                    }
                }
            });
        }

        return response;
    }, []);

    // const handleUpdateRestoreId = (id) => updateRestoreIDAPI(id).then(() => {
    //     dispatch({
    //         type: "MODIFY_USER",
    //         payload: {
    //             user: {
    //                 freshchat_restore_id: id
    //             }
    //         }
    //     });
    // });

    // ----------------------------------------------------------------------

    const checkAuthenticated = state.user ? "authenticated" : "unauthenticated";

    const status = state.loading ? "loading" : checkAuthenticated;

    const memoizedValue = useMemo(
        () => ( {
            user: state.user,
            method: "jwt",
            loading: status === "loading",
            authenticated: status === "authenticated",
            //
            login,
            register,
            logout,
            kicked,
            forgotPassword,
            resetPassword,
            resendActivateEmail,
            queryIsEnableRegister,
            activeUser,
            getUser,
            updateUser,
            modifyUser
        } ),
        [ state.user, status, login, register, logout, kicked, forgotPassword, resetPassword, resendActivateEmail, queryIsEnableRegister , activeUser, getUser, updateUser, modifyUser ]
    );

    // ----------------------------------------------------------------------

    useEffect(() => {
        initialize().then(null);
    }, [ initialize ]);

    useEffect(() => {
        if (isDatalayerMounted) {
            if (status === "authenticated") {
                window.dataLayer.push({
                    event: "user_data_init",
                    user_data: {
                        first_name: state.user?.first_name ?? "",
                        last_name: state.user?.last_name ?? "",
                        email: state.user?.email ?? ""
                    },
                    freshchat_user: {
                        restoreId: state.user?.freshchat_restore_id ?? ""
                    }
                });
            }

            if (status === "unauthenticated") {
                window.dataLayer.push({
                    event: "user_data_clear",
                    user_data: {
                        first_name: "",
                        last_name: "",
                        email: ""
                    },
                    freshchat_user: {
                        restoreId: ""
                    }
                });
            }
        }
    }, [ isDatalayerMounted, state.user, status ]);

    // useEffect(() => {
    //     if (isFcWidgetMounted) {
    //         if (status === "authenticated") {
    //             window.fcWidget.on("user:created", (resp) => {
    //                 if (status === "authenticated" && resp?.success) handleUpdateRestoreId(( resp?.status === 200 && resp?.data?.restoreId ) ? resp.data.restoreId : "").then(r => null);
    //             });
    //         }
    //     }
    // }, [ isFcWidgetMounted, state.user, status, updateUser ]);

    // ----------------------------------------------------------------------

    return <AuthContext.Provider value={ memoizedValue }>{ children }</AuthContext.Provider>;
}

AuthProvider.propTypes = {
    children: PropTypes.node
};
